import type { ContentStructure } from "../types/kbContentStructure";
import type {
  ExtractFieldsApiField,
  KbDraftField,
  KbFieldType,
  KbSectionSaveField,
  KbSectionSavePayload,
} from "../types/gameRuleKb";

const KB_FIELD_TYPES: ReadonlySet<string> = new Set(["Text", "Number", "Date", "Dropdown"]);

export const isKbFieldType = (value: string): value is KbFieldType =>
  KB_FIELD_TYPES.has(value);

export const mapExtractFieldToDraft = (field: ExtractFieldsApiField): KbDraftField => {
  const fieldType = isKbFieldType(field.field_type) ? field.field_type : "Text";
  const docPlaceholder = (field.doc_placeholder ?? "").trim() || undefined;
  return {
    id: field.temp_id || crypto.randomUUID(),
    name: field.name,
    fieldType,
    required: Boolean(field.required),
    hint: (field.hint ?? "").trim() || undefined,
    docPlaceholder,
    docAnchor: (field.suggested_doc_anchor ?? "").trim() || undefined,
    dropdownOptions:
      fieldType === "Dropdown" && Array.isArray(field.dropdown_options)
        ? field.dropdown_options.filter((o) => typeof o === "string" && o.trim())
        : undefined,
    isTagged: Boolean(field.is_tagged) || Boolean(docPlaceholder),
    value: (field.value ?? "").trim() || undefined,
  };
};

export const createEmptyKbDraftField = (name = "New Field"): KbDraftField => ({
  id: crypto.randomUUID(),
  name,
  fieldType: "Text",
  required: true,
  isTagged: false,
});

export const buildKbSectionSavePayload = (input: {
  readonly sectionName: string;
  readonly description: string;
  readonly contentPlain: string;
  readonly extractedContent: string | null;
  readonly contentStructure: ContentStructure | null;
  readonly extractedStructure: Record<string, unknown> | null;
  readonly tagNames: readonly string[];
  readonly fields: readonly KbDraftField[];
}): KbSectionSavePayload => {
  const name = input.sectionName.trim();
  const tag_names = [...new Set(input.tagNames.map((t) => t.trim()).filter(Boolean))];
  const content_plain = input.contentPlain.trim();
  const extracted_content = (input.extractedContent ?? "").trim() || undefined;

  if (!name) {
    throw new Error("Section name is required.");
  }
  if (!content_plain) {
    throw new Error("Section content is required.");
  }
  if (tag_names.length !== 1) {
    throw new Error("Exactly one tag is required.");
  }
  if (input.fields.length < 1) {
    throw new Error("At least one field is required.");
  }

  const fields: KbSectionSaveField[] = input.fields.map((field) => {
    if (!field.isTagged) {
      throw new Error(`Field "${field.name}" must be linked before save.`);
    }
    const fieldName = field.name.trim();
    if (!fieldName) {
      throw new Error("Every field must have a name.");
    }
    return {
      name: fieldName,
      field_type: field.fieldType,
      required: field.required,
      hint: field.hint?.trim() || undefined,
      doc_placeholder: field.docPlaceholder?.trim() || undefined,
      doc_anchor: field.docAnchor?.trim() || undefined,
      dropdown_options:
        field.fieldType === "Dropdown" && field.dropdownOptions?.length
          ? field.dropdownOptions
          : undefined,
      is_tagged: true,
    };
  });

  const description = input.description.trim();
  return {
    name,
    description: description || undefined,
    content_plain,
    extracted_content,
    content_structure: input.contentStructure ?? undefined,
    extracted_structure: input.extractedStructure ?? undefined,
    tag_names,
    fields,
  };
};

export const untaggedFieldNames = (fields: readonly KbDraftField[]): string[] =>
  fields.filter((f) => !f.isTagged).map((f) => f.name);

export const toKbDocAnchor = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "field";
};

export const toKbDocPlaceholder = (name: string): string => {
  const token = name.trim().toUpperCase().replace(/\s+/g, " ");
  return token ? `[${token}]` : "[FIELD]";
};

export const ensureUniqueAnchor = (
  anchor: string,
  usedAnchors: ReadonlySet<string>,
): string => {
  const base = anchor.trim() || "field";
  if (!usedAnchors.has(base)) {
    return base;
  }
  let suffix = 2;
  while (usedAnchors.has(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
};
